import { useCallback, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { AuthContext, type Profile } from "./auth-context";

export type { Profile } from "./auth-context";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  const fetchProfile = useCallback(async (userId: string) => {
    setProfileLoading(true);
    const { data, error } = await supabase
      .from("profiles")
      .select("id, display_name, username, avatar_url, selected_pillar, referral_code, total_impact_cents, current_badge_tier, premium_until, referral_months_earned")
      .eq("id", userId)
      .maybeSingle();
    if (!error) setProfile((data as Profile | null) ?? null);
    setProfileLoading(false);
  }, []);

  useEffect(() => {
    const attachPendingReferral = async (userId: string) => {
      let code: string | null = null;
      try {
        code = localStorage.getItem("ages_pending_referral");
      } catch {
        return;
      }
      if (!code) return;
      const { error } = await supabase.rpc("attach_referral_if_missing", { _ref_code: code });
      // Clear regardless — invalid/already-attributed codes shouldn't keep retrying.
      try { localStorage.removeItem("ages_pending_referral"); } catch { /* ignore */ }
      if (!error) fetchProfile(userId);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        const uid = newSession.user.id;
        setTimeout(() => fetchProfile(uid), 0);
        if (event === "SIGNED_IN") {
          setTimeout(() => attachPendingReferral(uid), 0);
        }
      } else {
        setProfile(null);
      }
    });

    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      setSession(existing);
      setUser(existing?.user ?? null);
      if (existing?.user) {
        fetchProfile(existing.user.id);
        // Cover the case where OAuth restored the session before the listener mounted.
        attachPendingReferral(existing.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const refreshProfile = useCallback(async () => {
    if (user) await fetchProfile(user.id);
  }, [user, fetchProfile]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{ session, user, profile, loading, profileLoading, refreshProfile, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
